import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { RouteComponentProps } from '~utils/withRouter';
import i18next from 'i18next';
import NumberFormat from 'react-number-format';
import ReactHtmlParser from 'react-html-parser';
import {convertNodeToElement} from 'react-html-parser';

import { withSpinner } from '~components/Spinner';
import ContentType from '~components/ContentType';
import Breadcrumb from '~components/Breadcrumb';
import PublicationBadge from '~components/PublicationBadge';
import Share from '~components/Share';
import { getDate } from '~utils/date';
import SliderArticles from '~components/SliderArticles';
import States from '~components/ProjectState';
import ProjectTracking from '~components/ProjectTracking';
import { Tracking } from '@shared/tracking-timeline';

import ModalContent from './components/ModalContent';
import styles from './styles.module.scss';
import { MILLION } from './constants';
import Map from './components/Map';
import updateCheckCircle from './assets/updateCheckCircle.svg';
import { BasicProps } from './types';
import { renderMany } from "~screens/Dashboard/screens/ProjectsIIRSA/screens/ProjectDetail/utils";
import {
    breadcrumbPaths,
    renderBool,
    renderText
} from "~screens/Dashboard/screens/ProjectsIIRSA/screens/ProjectDetail/utils";
import Gallery from "~screens/Dashboard/screens/ProjectsIIRSA/screens/ProjectDetail/components/Gallery";
import DropDown from "~components/Dropdown";
import SingleContent
    from "~screens/Dashboard/screens/Projects/screens/ProjectDetail/screens/TechnicalSheet/components/SingleContent";
import {
    AMBIENT_LICENSE,
    AREA,
    COUNTRIES, ESTIMATED_END_DATE, FINANCING, SECTOR, SUBSECTOR, TOTAL_INVESTMENT, PORTFOLIO,
    ACCOMPANIMENT, IIRSA_PROJECT_TYPE, STATE, STAGE, STUDIES_STATE, STUDIES_AMOUNT, END_DATE, STUDIES_FINISHED,
    TYPE, AMOUNT, SOURCE, PRIORITIZATION
} from "./constants";
import StateContent
    from "~screens/Dashboard/screens/Projects/screens/ProjectDetail/screens/TechnicalSheet/components/StateContent";
import GroupContent
    from "~screens/Dashboard/screens/Projects/screens/ProjectDetail/screens/TechnicalSheet/components/GroupContent";
import OwnersContent
    from "~screens/Dashboard/screens/Projects/screens/ProjectDetail/screens/TechnicalSheet/components/OwnersContent";
import linkIconBlue from "~screens/Dashboard/screens/Projects/screens/ProjectDetail/assets/linkIconBlue.svg";
import SectionsTable from "~screens/Dashboard/screens/ProjectsIIRSA/screens/ProjectDetail/components/SectionsTable";
import OldBreadcrumb from "~components/OldBreadcrumb";

interface Props extends RouteComponentProps, BasicProps {
    id: number;
}

class Layout extends Component<Props, { modelOpen: boolean; hasTracking: boolean }> {
    constructor(props: Props) {
        super(props);

        this.state = {
            modelOpen: false,
            hasTracking: false
        };
    }

    // The section stays hidden (but mounted, so it can fetch) until the project
    // turns out to have a Seguimiento.
    handleTrackingLoad = (tracking: Tracking | null) => {
        this.setState({ hasTracking: !!tracking });
    };

    handleOpenModal = () => {
        this.setState({ ...this.state, modelOpen: true });
    };

    handleCloseModal = () => {
        this.setState({ ...this.state, modelOpen: false });
    };

    SPANISH_TO_ENGLISH_INFRAESTRUCTURES = {
        ['transporte']: 'transport',
        ['energía']: 'energy',
        ['fronteras']: 'boundary',
        ['comunicaciones']: 'telecommunications',
    };

    SPANISH_TO_ENGLISH_STAGES = {
        ['perfil']: 'profiling',
        ['ejecución']: 'execution',
        ['pre-ejecución']: 'pre_execution',
        ['concluido']: 'finished',
    };

    toKey = (inf) => {
        return this.SPANISH_TO_ENGLISH_INFRAESTRUCTURES[inf.toLowerCase()];
    };

    stageToEnglish = (stage) => {
        const res = this.SPANISH_TO_ENGLISH_STAGES[stage.toLowerCase()];
        return res;
    };

    renderSegment = (segment) => {
        return <tr className={styles.tr}>
            <td className={styles.td}>{segment.segmentNumber}</td>
            <td className={styles.td}>{segment.segment}</td>
            <td className={styles.td}>{segment.description}</td>
            <td className={styles.td}>{segment.investment}</td>
            <td className={styles.td}>{segment.stage && segment.stage.name}</td>
        </tr>;
    };

    renderSingleFunding = (fund) => {
        return <>
            <SingleContent data={renderText(TYPE, fund.fundingType?.name || '')} className={styles.fundingType} />
            <SingleContent data={renderText(AMOUNT, fund.amount, false, true)} className={styles.fundingAmount} />
        </>;
    };

    renderFundingGroup = (group) => {
        const [name, funds] = group;
        return <>
            <SingleContent data={renderText(SOURCE, name)} className={styles.fundingSource} valueClassName={styles.fundingSourceTitle} />
            {funds.map(this.renderSingleFunding)}
        </>;
    };

    renderFunding = (fundings) => {
        if (fundings) {
            let groupedFundings = {};
            for (const funding of fundings) {
                const funderName = funding.funder?.name || '';
                if (funderName in groupedFundings) {
                    groupedFundings[funderName].push(funding);
                } else {
                    groupedFundings[funderName] = [funding];
                }
            }

            return Object.entries(groupedFundings).map((group) => this.renderFundingGroup(group))
        }
        return fundings && fundings.map(this.renderSingleFunding)
    };

    renderDescription = () => {
        let url;
        if (this.props.projectDetail.url) {
            url = this.props.projectDetail.url;
            url = url.includes('https') ? url : 'https://' + url;
        }
        const objectives = this.props.projectDetail ? this.props.projectDetail.goal.replace('<p>', '').replace('</p>', '') : '';
        const content = this.props.projectDetail ? this.props.projectDetail.description.replace('<p>', '').replace('</p>', '') : '';
        return (
            <div className={`column ${styles.description}`}>
                <h3 className="title-medium m-top-3 m-bottom-3">{i18next.t('technicalSheet:objectives')}</h3>
                <p className="text-small rich-text">{ReactHtmlParser(objectives)}</p>
                <h3 className="title-medium m-top-3 m-bottom-3">{i18next.t('technicalSheet:description')}</h3>
                <p className="text-small rich-text">{ReactHtmlParser(content)}</p>
                {url ?
                    <>
                        <h3 className="title-medium m-top-3 m-bottom-3">{i18next.t('technicalSheet:url')}</h3>
                        <a href={url} className="text-medium" target="_blank">
                            <img src={linkIconBlue} style={{ width: '15px', marginRight: '8px' }} />
                            {url}
                        </a>
                    </>
                    :
                    <></>
                }

            </div>
        )
    };

    render() {
        const { projectDetail, id } = this.props;
        const { modelOpen } = this.state;

        let hasGalleryItems = projectDetail && ((projectDetail.documents && projectDetail.documents.length > 0)
            || (projectDetail.images && projectDetail.images.length > 0)
            || (projectDetail.links && projectDetail.links.length > 0));

        const statusComments = (this.props.projectDetail && this.props.projectDetail.statusComments) ? this.props.projectDetail.statusComments.replace('<p>', '').replace('</p>', '') : '';
        const studiesStatusComments = (this.props.projectDetail && this.props.projectDetail.studiesStatusComments) ? this.props.projectDetail.studiesStatusComments.replace('<p>', '').replace('</p>', '') : '';
        const fundingComments = (this.props.projectDetail && this.props.projectDetail.fundingComments) ? this.props.projectDetail.fundingComments.replace('<p>', '').replace('</p>', '') : '';

        return projectDetail && (
            <div className={styles.projectsConteiner}>
                <div className={styles.projectDetailContainer}>
                    <div className={styles.projectHeader}>
                        <div className={styles.projectHeaderInfo}>
                            <div>
                                <OldBreadcrumb paths={[
                                    { name: i18next.t('projectDetail:projects'), url: `/routes/${projectDetail.route.id}/show` },
                                    { name: i18next.t('technicalSheet:projectSheet') }
                                ]} />
                            </div>
                            <div>
                                <h1 className={styles.projectTitle}>{projectDetail.name}</h1>
                            </div>
                            <div>
                                <PublicationBadge status={projectDetail.publicationStatus}/>
                            </div>
                            <div>
                                <h1><span className={`title-xxxbig ${styles.code}`}>{projectDetail.code}</span></h1>
                            </div>
                            <div>

                            </div>
                        </div>
                        <div className={styles.projectHeaderMap}>
                            {projectDetail.kmlSimplified ?
                                <>
                                    <div>
                                        <Map project={projectDetail} />
                                    </div>
                                </>
                                :
                                <></>

                            }
                            {projectDetail.kml ?
                                <>
                                    <button type="button" className="link"
                                        onClick={this.handleOpenModal}>
                                        {i18next.t('projectDetail:zoomMap')}
                                    </button>
                                </>
                                :
                                <></>
                            }
                        </div>
                    </div>
                    <div className={styles.projectInfo}>
                        <div className={styles.projectInfoItem}>
                            <ContentType infrastructure={projectDetail.iirsaSector} subsector={projectDetail.iirsaSubsector} />
                        </div>
                        <div className={styles.projectInfoSeparator}></div>
                        <div className={styles.projectInfoItem}>
                            <h3 className={styles.projectInfoItemTitle}>{projectDetail.iirsaCountries.length}</h3>
                            <h3 className={`${styles.projectInfoItemSubtitle} ${styles.statsCategory}`}>
                                {i18next.t('projectDetail:countries')}
                            </h3>
                        </div>
                        <div className={styles.projectInfoSeparator}></div>
                        <div className={styles.projectInfoItem}>
                            <h3 className={`${styles.projectInfoItemSubtitle} ${styles.statsCategory}`}>
                                {i18next.t('projectDetail:dollarsInvested')}
                            </h3>
                            <NumberFormat
                                className={styles.projectInfoItemTitle}
                                value={projectDetail.totalInvestment}
                                displayType="text"
                                decimalSeparator=","
                                decimalScale={2}
                                thousandSeparator='.'
                                prefix='$'
                            />
                        </div>
                        <div className={styles.projectInfoSeparator}></div>
                        <div className={styles.projectState}>
                            <StateContent stageOrder={projectDetail.iirsaStage.order} />
                        </div>
                    </div>
                </div>
                <div className={styles.projectDescription}>
                    <div className={`${this.state.hasTracking ? 'm-bottom-0' : 'm-bottom-29'} ${styles.mainContainer}`}>
                        <div className={`column ${styles.leftColumn}`}>
                            <div className="m-bottom-5">
                                <DropDown
                                    title={i18next.t('technicalSheet:range')}
                                    textClassName="subtitle"
                                    relativeDropdown
                                    marginBottom={'0'}
                                    open
                                >
                                    <div className={styles.dropdownContent}>
                                        {this.renderDescription()}
                                    </div>
                                </DropDown>
                            </div>
                            <div className="m-bottom-5">
                                <DropDown
                                    title={i18next.t('technicalSheet:comments')}
                                    textClassName="subtitle"
                                    relativeDropdown
                                    marginBottom={'0'}
                                >
                                    <div className={styles.dropdownContent}>
                                        {projectDetail.statusComments ? <>
                                            <h3 className="title-medium m-top-3 m-bottom-3">{i18next.t('technicalSheet:state')}</h3>
                                            <p className="text-small rich-text">{ReactHtmlParser(statusComments)}</p>
                                        </> : <></>}
                                        {projectDetail.studiesStatusComments ? <>
                                            <h3 className="title-medium m-top-3 m-bottom-3">{i18next.t('technicalSheet:studies')}</h3>
                                            <p className="text-small rich-text">{ReactHtmlParser(studiesStatusComments)}</p>
                                        </> : <></>}
                                        {projectDetail.fundingComments ? <>
                                            <h3 className="title-medium m-top-3 m-bottom-3">{i18next.t('technicalSheet:financing')}</h3>
                                            <p className="text-small rich-text">{ReactHtmlParser(fundingComments)}</p>
                                        </> : <></>}
                                    </div>
                                </DropDown>
                            </div>
                            {/* <div className="column desktop">
                                <h2 className="title-medium">{i18next.t('technicalSheet:range').toString().toUpperCase()}</h2>
                                <div className="column m-bottom-6">{this.renderDescription()}</div>
                            </div> */}
                            {/* <div className="column desktop">
                                <h2 className="title-medium">COMENTARIOS</h2>
                                <div className="column m-bottom-6">
                                    <div className={`column ${styles.description}`}>
                                        {projectDetail.statusComments ? <>
                                            <h3 className="title-medium m-top-3 m-bottom-3">Estado</h3>
                                            <p className="text-small rich-text">{ReactHtmlParser(statusComments)}</p>
                                        </> : <></>}
                                        {projectDetail.studiesStatusComments ? <>
                                            <h3 className="title-medium m-top-3 m-bottom-3">Estudios</h3>
                                            <p className="text-small rich-text">{ReactHtmlParser(studiesStatusComments)}</p>
                                        </> : <></>}
                                        {projectDetail.fundingComments ? <>
                                            <h3 className="title-medium m-top-3 m-bottom-3">Financiamiento</h3>
                                            <p className="text-small rich-text">{ReactHtmlParser(fundingComments)}</p>
                                        </> : <></>}

                                    </div>
                                </div>
                            </div> */}
                            {projectDetail.iirsaSegments && projectDetail.iirsaSegments.length ?
                                <div className="m-bottom-5">
                                    <DropDown
                                        title={i18next.t('technicalSheet:sections')}
                                        textClassName="subtitle"
                                        relativeDropdown
                                        marginBottom={'0'}
                                    >
                                        <div className={styles.dropdownContent}>
                                            <SectionsTable sections={projectDetail.iirsaSegments} />
                                        </div>
                                    </DropDown>
                                </div> : <></>}
                            {(projectDetail.iirsaLink && projectDetail.iirsaLink.length) || hasGalleryItems ?
                                <div className="m-bottom-5">
                                    <DropDown
                                        title={i18next.t('technicalSheet:complementaryInfo')}
                                        textClassName="subtitle"
                                        relativeDropdown
                                        marginBottom={'0'}
                                    >
                                        <div className={styles.dropdownContent}>
                                            <div className="column m-bottom-6">
                                                <div className={`column ${styles.description}`}>
                                                    {projectDetail.iirsaLink && <>
                                                        <h3 className="title-medium m-top-3 m-bottom-3">{i18next.t('technicalSheet:links')}</h3>
                                                        {projectDetail.iirsaLink.map(function (link, index) {
                                                            return <p className="text-medium rich-text"><a href={link.link}
                                                                target={'_blank'}>{link.link}</a>
                                                            </p>;
                                                        })}

                                                    </>}

                                                </div>
                                            </div>
                                            {hasGalleryItems ?
                                                <Gallery items={projectDetail.galleryItems} />
                                                :
                                                null
                                            }
                                        </div>
                                    </DropDown>
                                </div> : <></>}
                            {projectDetail.sections && projectDetail.sections.length > 0 ?
                                <SectionsTable sections={projectDetail.sections} />
                                :
                                null
                            }

                        </div>
                        <div className={`column ${styles.rightColumn}`}>
                            <div className="column">
                                <div className="m-bottom-5">
                                    <DropDown
                                        title={i18next.t('technicalSheet:general')}
                                        textClassName="subtitle"
                                        relativeDropdown
                                        marginBottom={'0'}
                                        open
                                    >
                                        <SingleContent
                                            data={renderMany(COUNTRIES, projectDetail.iirsaCountries.map(p => p['name']))} />
                                        <SingleContent data={renderText(AREA, projectDetail.iirsaScope.name)} />
                                        <SingleContent data={renderText(SECTOR, projectDetail.iirsaSector.name)} />
                                        <SingleContent data={renderText(SUBSECTOR, projectDetail.iirsaSubsector.name)} />
                                        <SingleContent
                                            data={renderText(IIRSA_PROJECT_TYPE, projectDetail['iirsaProjectType'].name)} />
                                    </DropDown>
                                </div>
                                <div className="m-bottom-5">
                                    <DropDown
                                        title={i18next.t('technicalSheet:stateOfProject')}
                                        textClassName="subtitle"
                                        relativeDropdown
                                        marginBottom={'0'}
                                    >
                                        <SingleContent data={renderText(STAGE, projectDetail.iirsaStage.name)} />
                                        <SingleContent
                                            data={renderText(STATE, projectDetail.iirsaStatus ? projectDetail.iirsaStatus.name : "")} />
                                        <SingleContent
                                            data={renderText(END_DATE, projectDetail.endDate ? getDate(projectDetail.endDate, 'MM/YYYY') : '-')} />
                                        <SingleContent
                                            data={renderText(PRIORITIZATION, projectDetail.prioritization ? projectDetail.prioritization.name : '-')} />
                                        <SingleContent
                                            data={renderBool(AMBIENT_LICENSE, projectDetail.hasAmbientalLicense)} />
                                        <SingleContent
                                            data={renderText(STUDIES_STATE, projectDetail.iirsaStudiesState ? projectDetail.iirsaStudiesState.name.toUpperCase() : "")} />
                                        <SingleContent
                                            data={renderBool(STUDIES_FINISHED, projectDetail.studiesFinished)} />
                                        <SingleContent
                                            data={renderText(STUDIES_AMOUNT, projectDetail.studiesAmount, false, true)} />
                                    </DropDown>
                                </div>
                                <div className="m-bottom-5">
                                    <DropDown
                                        title={i18next.t('technicalSheet:finance')}
                                        textClassName="subtitle"
                                        relativeDropdown
                                        marginBottom={'0'}
                                    >
                                        <SingleContent
                                            data={renderText(TOTAL_INVESTMENT, projectDetail.totalInvestment, false, true)} 
                                            className={styles.totalInvestment}
                                            titleClassName={styles.totalInvestmentTitle}
                                            valueClassName={styles.totalInvestmentTitle}/>
                                        {/*<SingleContent data={renderText(FINANCING, projectDetail[FINANCING], true)} />*/}
                                        {this.renderFunding(projectDetail.iirsaProjectFunding)}
                                    </DropDown>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="m-bottom-29" hidden={!this.state.hasTracking}>
                        <DropDown
                            title={i18next.t('tracking:sectionTitle')}
                            textClassName="subtitle"
                            relativeDropdown
                            marginBottom={'0'}
                            open
                        >
                            <div className={styles.dropdownContent}>
                                <ProjectTracking projectId={String(this.props.id)} onLoad={this.handleTrackingLoad} />
                            </div>
                        </DropDown>
                    </div>
                </div>
                <ModalContent project={projectDetail} isOpen={modelOpen} closeModal={this.handleCloseModal} />
            </div>
        );
    }
}

export default withSpinner({ WrappedComponent: Layout });
